import { PlanLimit, PlanTier } from '@fluenceur/types';
import { createSerializer, parseAsString, parseAsStringLiteral } from 'nuqs';

export const paywallTypes = ['plans', ...Object.values(PlanLimit)] as const;
export const paywallPlans = ['free', 'essential', 'growth'] as const;
export type PaywallType = (typeof paywallTypes)[number];
export type PaywallPlan = (typeof paywallPlans)[number];

/** Only presentation intent belongs in the URL. Rights and quotas come from the API. */
export const paywallParsers = {
  paywall: parseAsStringLiteral(paywallTypes),
  plan: parseAsStringLiteral(paywallPlans),
  minimumPlan: parseAsStringLiteral(['essential', 'growth'] as const),
  operationId: parseAsString,
  session_id: parseAsString,
  shopify_free_approval: parseAsString,
  success: parseAsString,
  cancelled: parseAsString,
};
export const closedPaywallState = { paywall: null, plan: null, minimumPlan: null } as const;
export const consumedBillingReturnState = {
  operationId: null,
  session_id: null,
  shopify_free_approval: null,
  success: null,
  cancelled: null,
} as const;
const serialize = createSerializer(paywallParsers);

/** nuqs 2.4 serializes the query only; preserve the origin's fragment separately. */
export function serializePaywallUrl(
  base: string,
  values: {
    paywall?: PaywallType | null;
    plan?: PaywallPlan | null;
    minimumPlan?: 'essential' | 'growth' | null;
  }
): string {
  const fragmentStart = base.indexOf('#');
  const origin = fragmentStart === -1 ? base : base.slice(0, fragmentStart);
  const fragment = fragmentStart === -1 ? '' : base.slice(fragmentStart);
  return serialize(origin, values) + fragment;
}

export type UpgradeFeature =
  | 'multi_brand'
  | 'flows'
  | 'team_members'
  | 'advanced_analytics'
  | 'priority_support'
  | 'generic';

export interface PaywallScope {
  userId?: string;
  organizationId?: string;
  brandId?: string;
}

export const paywallScopeKey = (scope: PaywallScope | null) =>
  JSON.stringify([scope?.userId ?? null, scope?.organizationId ?? null, scope?.brandId ?? null]);

/** Legacy feature entry points either name a real limit or choose a paid plan. */
export function upgradePaywallFor(
  feature: UpgradeFeature = 'generic',
  preferredTier?: PlanTier,
  currentTier = PlanTier.FREE
): { paywall: PaywallType; plan: PaywallPlan | null } {
  if (feature === 'flows') return { paywall: PlanLimit.FLOWS, plan: null };
  if (feature === 'team_members') return { paywall: PlanLimit.TEAM_MEMBERS, plan: null };
  const tier =
    preferredTier ??
    (feature !== 'generic' || currentTier === PlanTier.ESSENTIAL || currentTier === PlanTier.GROWTH
      ? PlanTier.GROWTH
      : PlanTier.ESSENTIAL);
  return {
    paywall: 'plans',
    plan: tier === PlanTier.GROWTH ? 'growth' : tier === PlanTier.FREE ? 'free' : 'essential',
  };
}
